import { useCallback, useEffect, useState } from 'react';
import { Eye, Edit, UserX, UserPlus, Loader2, Search, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { dashboardRoutes } from '../../constants/routes';
import { shellAssets } from '../../constants/assets';
import { useConfirmDialog } from '../../context/ConfirmDialogContext';
import {
  apiGetCustomers,
  apiCreateCustomer,
  apiUpdateCustomer,
  apiDeactivateCustomer,
  apiReactivateCustomer,
  type CustomerRecord,
  type CreateCustomerPayload,
} from '../../api/customers/customersApi';
import { CustomerFormModal } from './CustomerFormModal';
import { ViewCustomerModal } from './ViewCustomerModal';

const currentUser = {
  name: 'Admin',
  email: 'Admin@123gmail.com',
  avatar: shellAssets.avatar,
};

const PAGE_SIZE = 20;

const CustomersPage = () => {
  const { confirm, showSuccess } = useConfirmDialog();

  const [customers, setCustomers] = useState<CustomerRecord[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [viewingCustomer, setViewingCustomer] = useState<CustomerRecord | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<CustomerRecord | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const loadCustomers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const result = await apiGetCustomers({
      search: search || undefined,
      isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
      page,
    });

    if (result.success && result.data) {
      setCustomers(result.data.results);
      setCount(result.data.count);
    } else {
      setError(result.message);
      setCustomers([]);
    }

    setIsLoading(false);
  }, [search, statusFilter, page]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const handleDeactivate = async (customer: CustomerRecord) => {
    const name =
      [customer.first_name, customer.last_name].filter(Boolean).join(' ') || customer.username;

    const confirmed = await confirm({
      title: 'Deactivate Customer',
      message: `Are you sure you want to deactivate ${name}? Their account and history will be preserved.`,
      confirmText: 'Deactivate Customer',
      cancelText: 'Cancel',
      variant: 'danger',
      icon: 'user-minus',
    });

    if (!confirmed) return;

    const result = await apiDeactivateCustomer(customer.id);

    if (result.success) {
      await showSuccess({
        title: 'Customer Deactivated',
        message: `${name} has been deactivated.`,
      });
      loadCustomers();
    } else {
      toast.error(result.message || 'Failed to deactivate customer.');
    }
  };

  const handleReactivate = async (customer: CustomerRecord) => {
    const name =
      [customer.first_name, customer.last_name].filter(Boolean).join(' ') || customer.username;

    const confirmed = await confirm({
        title: 'Reactivate Customer',
        message: `Are you sure you want to reactivate ${name}?`,
        confirmText: 'Reactivate Customer',
        cancelText: 'Cancel',
        variant: 'info',
        icon: 'alert',
    });

    if (!confirmed) return;

    const result = await apiReactivateCustomer(customer.id);

    if (result.success) {
      await showSuccess({
        title: 'Customer Reactivated',
        message: `${name} has been reactivated.`,
      });
      loadCustomers();
    } else {
      toast.error(result.message || 'Failed to reactivate customer.');
    }
  };

  const handleFormSubmit = async (payload: CreateCustomerPayload) => {
    const result = editingCustomer
      ? await apiUpdateCustomer(editingCustomer.id, payload)
      : await apiCreateCustomer(payload);

    if (result.success) {
      loadCustomers();
    }

    return result;
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <DashboardLayout
      activeNavId="customers"
      contentGap={15}
      subtitle={
        <Breadcrumb
          items={[{ label: 'Dashboard', path: dashboardRoutes.overview }, { label: 'Customers' }]}
        />
      }
      title="Customers"
      user={currentUser}
    >
      <div className="flex min-h-0 flex-col gap-[13px]">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] bg-white p-4">
          <div className="flex items-center gap-2 rounded-[10px] border border-[#E0E0E0] px-3 py-2">
            <Search size={16} className="text-[#757575]" />
            <input
              className="w-[220px] font-host text-[14px] outline-none"
              placeholder="Search name, email, phone…"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
            />
          </div>

          <select
            className="rounded-[10px] border border-[#E0E0E0] px-3 py-2 font-host text-[14px]"
            value={statusFilter}
            onChange={(e) => {
              setPage(1);
              setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
            }}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            onClick={() => {
              setEditingCustomer(null);
              setIsFormOpen(true);
            }}
            className="flex items-center gap-2 rounded-[10px] bg-[#FF6A1A] px-4 py-2 font-host text-[14px] font-medium text-white hover:bg-[#e85f16]"
          >
            <Plus size={16} />
            Add Customer
          </button>
        </div>

        <div className="overflow-x-auto rounded-[12px] bg-white p-[10px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-[#FF6A1A]" size={32} />
            </div>
          ) : error ? (
            <p className="py-10 text-center font-host text-[14px] text-red-600">{error}</p>
          ) : customers.length === 0 ? (
            <p className="py-10 text-center font-host text-[14px] text-[#757575]">
              No customers found.
            </p>
          ) : (
            <table className="w-full min-w-[900px] border-collapse">
              <thead>
                <tr className="border-b border-[#F0F0F0] text-left font-host text-[12px] font-semibold uppercase text-[#757575]">
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2">Country</th>
                  <th className="px-3 py-2">Verified</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => {
                  const name =
                    [customer.first_name, customer.last_name].filter(Boolean).join(' ') ||
                    customer.profile_full_name ||
                    customer.username;

                  return (
                    <tr key={customer.id} className="border-b border-[#F5F5F5] font-host text-[14px]">
                      <td className="px-3 py-3 font-medium text-[#1A1A1A]">{name}</td>
                      <td className="px-3 py-3 text-[#1A1A1A]">{customer.email}</td>
                      <td className="px-3 py-3 text-[#1A1A1A]">{customer.phone || '—'}</td>
                      <td className="px-3 py-3 text-[#1A1A1A]">{customer.country || '—'}</td>
                      <td className="px-3 py-3">
                        <span
                          className={
                            customer.is_verified
                              ? 'rounded-full bg-green-100 px-2 py-1 text-[12px] text-green-700'
                              : 'rounded-full bg-gray-100 px-2 py-1 text-[12px] text-gray-600'
                          }
                        >
                          {customer.is_verified ? 'Verified' : 'Unverified'}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={
                            customer.is_active
                              ? 'rounded-full bg-green-100 px-2 py-1 text-[12px] text-green-700'
                              : 'rounded-full bg-red-100 px-2 py-1 text-[12px] text-red-700'
                          }
                        >
                          {customer.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-[#757575]">
                        {new Date(customer.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            title="View"
                            onClick={() => setViewingCustomer(customer)}
                            className="rounded-[8px] p-2 text-[#1A1A1A] hover:bg-[#F2F2F2]"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            title="Edit"
                            onClick={() => {
                              setEditingCustomer(customer);
                              setIsFormOpen(true);
                            }}
                            className="rounded-[8px] p-2 text-[#1A1A1A] hover:bg-[#F2F2F2]"
                          >
                            <Edit size={16} />
                          </button>
                          {customer.is_active ? (
                            <button
                              title="Deactivate"
                              onClick={() => handleDeactivate(customer)}
                              className="rounded-[8px] p-2 text-red-600 hover:bg-red-50"
                            >
                              <UserX size={16} />
                            </button>
                          ) : (
                            <button
                              title="Reactivate"
                              onClick={() => handleReactivate(customer)}
                              className="rounded-[8px] p-2 text-green-600 hover:bg-green-50"
                            >
                              <UserPlus size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {!isLoading && !error && count > 0 && (
          <div className="flex items-center justify-between px-2 font-host text-[13px] text-[#757575]">
            <span>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, count)} of {count}
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-[8px] border border-[#E0E0E0] px-3 py-1 disabled:opacity-40"
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="rounded-[8px] border border-[#E0E0E0] px-3 py-1 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <CustomerFormModal
        customer={editingCustomer}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
      />

      <ViewCustomerModal
        customer={viewingCustomer}
        isOpen={Boolean(viewingCustomer)}
        onClose={() => setViewingCustomer(null)}
      />
    </DashboardLayout>
  );
};

export default CustomersPage;
